import React, { useState, useEffect } from 'react'
import type { MediaItem } from '@/types'
import { getAdminContent, uploadImage } from '@/utils/api'
import ConfirmModal from '@/admin/ConfirmModal'

function MediaManager() {
  const [images, setImages] = useState<MediaItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)

  const loadMedia = async () => {
    setLoading(true)
    setUploadError(null)
    try {
      const data = await getAdminContent()
      setImages(data.media || [])
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error al cargar medios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMedia()
  }, [])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setUploadError(null)

    try {
      const uploaded = await uploadImage(file, file.name)
      setImages((prev) => [...prev, uploaded])
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error al subir la imagen')
    } finally {
      setIsUploading(false)
    }
  }

  const confirmDelete = (id: number) => {
    setDeleteTargetId(id)
  }

  const handleDelete = async () => {
    if (!deleteTargetId) return

    setDeletingId(deleteTargetId)
    try {
      await fetch(`https://el-horcon-backend.elhorconcriollo.workers.dev/api/admin/media/${deleteTargetId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      setImages((prev) => prev.filter((img) => img.id !== deleteTargetId))
      setDeleteTargetId(null)
    } catch (error) {
      console.error('Error deleting image:', error)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="min-h-screen">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-text-primary mb-1">Gestión de Medios</h1>
          <p className="text-text-secondary">Subí y administrá las imágenes del sitio</p>
        </div>
        <label className="bg-primary text-white px-4 py-2.5 rounded-lg font-medium hover:bg-primary-dark transition cursor-pointer shadow-sm">
          {isUploading ? (
            <span className="flex items-center">
              <i className="ph ph-spinner animate-spin mr-2"></i>
              Subiendo...
            </span>
          ) : (
            <span className="flex items-center">
              <i className="ph ph-upload mr-2"></i>
              Seleccionar imagen
            </span>
          )}
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        </label>
      </div>

      {uploadError && (
        <div className="mb-6 p-4 bg-error/10 text-error rounded-lg border border-error/20">
          {uploadError}
        </div>
      )}

      {loading ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <i className="ph ph-spinner animate-spin text-3xl text-primary mb-4"></i>
          <p className="text-text-secondary">Cargando medios...</p>
        </div>
      ) : images.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <i className="ph ph-image text-5xl text-gray-300 mb-4"></i>
          <p className="text-text-secondary">No hay imágenes subidas aún</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image) => (
            <div key={image.id} className="glass-card rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <img
                src={`https://el-horcon-backend.elhorconcriollo.workers.dev${image.url}`}
                alt={image.alt_text}
                className="w-full h-32 object-cover"
              />
              <div className="p-3 flex justify-between items-center bg-white">
                <span className="text-sm text-text-primary truncate flex-1" title={image.filename}>
                  {image.filename}
                </span>
                <button
                  onClick={() => confirmDelete(image.id)}
                  disabled={deletingId === image.id}
                  className="text-error hover:bg-error/10 p-1.5 rounded disabled:opacity-50"
                  title="Eliminar imagen"
                >
                  <i className="ph ph-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTargetId}
        title="Eliminar imagen"
        message="¿Estás seguro de que querés eliminar esta imagen? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTargetId(null)}
        isConfirming={deletingId === deleteTargetId}
      />
    </div>
  )
}

export default MediaManager